// tiff.ts
import {
  TypedArrayArrayWithDimensions,
  fromUrl,
} from 'geotiff';
import { useCallback, useState } from 'react';
import proj4 from 'proj4';
import { colorMapping, isPointInPolygon } from './helper';
import { polygonType } from '../../../../types/mvpMap';
// import { appLogEvent } from '../../../../utils/analytics';
import { useAlertHelper } from '../../../../utils/functions/hooks';

// Define the source and destination projections
const sourceProjection = 'EPSG:3857'; // WGS 84 / Pseudo-Mercator
const destinationProjection = 'EPSG:4326'; // WGS 84 (lat/lng)

export const useTiffHelper = () => {
  const [center, setCenter] = useState<{
    lat: number;
    lng: number;
  }>({ lat: 0, lng: 0 });

  const { showError } = useAlertHelper();

  const onMapLoad = useCallback(
    async (
      map: google.maps.Map,
      tiffFileUrl: string,
      polygon: polygonType[],
      indexName: string | null | undefined,
      layerIndex: number = 0 // Add layerIndex to control stacking
    ) => {
      try {
        // Fetch and parse the GeoTIFF file
        const tiff = await fromUrl(tiffFileUrl);
        const image = await tiff.getImage();

        // Get the width and height of the image
        const width = image.getWidth();
        const height = image.getHeight();

        // Read the GeoTIFF metadata (tie points and pixel scales)
        const tiePoints = image.getTiePoints();
        const pixelScale =
          image.getFileDirectory().ModelPixelScale;

        // Extract the top-left corner in EPSG:3857 (x, y in meters)
        const topLeftProjected = [
          tiePoints[0].x,
          tiePoints[0].y,
        ];

        // Calculate the bottom-right corner in EPSG:3857 (x, y in meters)
        const bottomRightProjected = [
          tiePoints[0].x + pixelScale[0] * width,
          tiePoints[0].y - pixelScale[1] * height,
        ];

        // Reproject coordinates to EPSG:4326 (lat/lng)
        const topLeft = proj4(
          sourceProjection,
          destinationProjection,
          topLeftProjected
        );
        const bottomRight = proj4(
          sourceProjection,
          destinationProjection,
          bottomRightProjected
        );

        // Calculate the center of the GeoTIFF
        const centerLat = (topLeft[1] + bottomRight[1]) / 2;
        const centerLng = (topLeft[0] + bottomRight[0]) / 2;

        // Read raster data (assume single band for index like NDVI)
        const rasterData =
          (await image.readRasters()) as TypedArrayArrayWithDimensions;

        // Precision factor for canvas
        const clippingPrecisionFactor = 16;
        const canvasWidth = width * clippingPrecisionFactor;
        const canvasHeight =
          height * clippingPrecisionFactor;

        // Create a canvas to draw the GeoTIFF
        const canvas = document.createElement('canvas');
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        const context = canvas.getContext('2d');

        if (!context) {
          throw new Error(
            'Failed to get 2D canvas context'
          );
        }

        const imageData = context.createImageData(
          canvasWidth,
          canvasHeight
        );
        const data = imageData.data;

        const startLat = topLeft[1];
        const startLng = topLeft[0];
        const latScale =
          (bottomRight[1] - topLeft[1]) / canvasHeight;
        const lngScale =
          (bottomRight[0] - topLeft[0]) / canvasWidth;

        // Fill the canvas with TIFF data
        const rasterDataAtPixels = rasterData[0];
        for (
          let i = 0;
          i < canvasWidth * canvasHeight;
          i++
        ) {
          const colIndex = i % canvasWidth;
          const rowIndex = (i - colIndex) / canvasWidth;
          const currPixelLatLng = {
            lat: startLat + latScale * rowIndex,
            lng: startLng + lngScale * colIndex,
          };
          const isPointInsidePolygon = isPointInPolygon(
            currPixelLatLng,
            polygon
          );
          const actualRowIndex = Math.floor(
            rowIndex / clippingPrecisionFactor
          );
          const actualColIndex = Math.floor(
            colIndex / clippingPrecisionFactor
          );
          const rasterDataIndex =
            actualRowIndex * width + actualColIndex;
          const rasterDataAtCurrIndex =
            rasterDataAtPixels[rasterDataIndex];
          const color = colorMapping(
            indexName,
            rasterDataAtCurrIndex
          );
          data[i * 4] = color.R; // Red channel
          data[i * 4 + 1] = color.G; // Green channel
          data[i * 4 + 2] = color.B; // Blue channel
          data[i * 4 + 3] = isPointInsidePolygon ? 255 : 0; // Alpha channel
        }

        context.putImageData(imageData, 0, 0);

        // Convert canvas to a data URL
        const imageUrl = canvas.toDataURL();

        // Create a ground overlay with zIndex to control layering
        const groundOverlay = new google.maps.GroundOverlay(
          imageUrl,
          {
            north: topLeft[1],
            south: bottomRight[1],
            east: bottomRight[0],
            west: topLeft[0],
          }
        );

        // Set zIndex to ensure proper stacking
        groundOverlay.set('zIndex', layerIndex);

        // Add the overlay to the map
        groundOverlay.setMap(map);

        // Adjust map bounds to fit the GeoTIFF
        const bounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(
            bottomRight[1],
            bottomRight[0]
          ),
          new google.maps.LatLng(topLeft[1], topLeft[0])
        );

        // Only update center and fit bounds for the first layer to avoid redundant adjustments
        if (layerIndex === 0) {
          map.fitBounds(bounds);
          setCenter({ lat: centerLat, lng: centerLng });
          google.maps.event.addListenerOnce(
            map,
            'idle',
            () => {
              map.setZoom(19); // Fixed zoom level
            }
          );
        }
      } catch (error) {
        // showError('Error loading Map: ' + error);
        // appLogEvent('ErrorLoadingMap', { error });
        console.error('Error loading Map:', error);
      }
    },
    [showError]
  );

  return {
    onMapLoad,
    center,
  };
};
